// src/components/FilterBar.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Select, { components } from "react-select";
import * as XLSX from "xlsx";

// ---------------- Styled ----------------
const FilterContainer = styled.div`
  background-color: ${({ $light }) => ($light ? "#FFFFFF" : "#2D354A")};
  padding: 25px 30px;
  border-radius: 12px;
  margin-bottom: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  color: ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
  font-size: 14px;
`;

const FilterInput = styled.input`
  padding: 10px 14px;
  background-color: ${({ $light }) => ($light ? "#FFFFFF" : "#191B27")};
  border: 2px solid ${({ $light }) => ($light ? "#2D354A" : "#191B27")};
  border-radius: 6px;
  color: ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  height: 42px;                /* ✅ same as Select height */
  width: 100%;                 /* ✅ match Select width */
  box-sizing: border-box;      /* padding included in width */
`;




const ClearButton = styled.button`
  margin-top: 2px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
  border-radius: 4px;
  color: ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background-color: ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
    color: ${({ $light }) => ($light ? "#DCE4F2" : "#191B27")};
  }
`;

const ExportButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ExportButton = styled.button`
  padding: 10px 16px;
  background-color: ${({ $light }) => ($light ? "#FFFFFF" : "#2D354A")};
  border: 2px solid ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
  border-radius: 6px;
  color: ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  cursor: pointer;
  &:hover {
    background-color: ${({ $light }) => ($light ? "#191B27" : "#DCE4F2")};
    color: ${({ $light }) => ($light ? "#DCE4F2" : "#191B27")};
  }
`;

// ---------------- React-Select Custom Bits ----------------
const CheckboxOption = (props) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      readOnly
      style={{ marginRight: 8 }}
    />
    {props.label}
  </components.Option>
);

const ValueContainerSummary = (props) => {
  const { children, selectProps, getValue } = props;
  const selected = getValue();

  let displayText = null;

  if (!selectProps.inputValue && selected.length === 0) {
    // No selections → placeholder
    displayText = "Apply Filter(s)";
  } else if (selected.length === 1) {
    // One selection → show its label
    displayText = selected[0].label;
  } else if (selected.length > 1) {
    // Multiple selections → show count
    displayText = `${selected.length} Selected`;
  }

  return (
    <components.ValueContainer {...props}>
      {displayText && (
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "14px",
            fontWeight: 500,
            color: selectProps.$light ? "#000000" : "FFFFFF", // black in light, white in dark
            opacity: 1, // keep solid
            pointerEvents: "none",
            whiteSpace: "nowrap",       // ✅ force single line
            overflow: "hidden",         // ✅ prevent wrapping
            textOverflow: "ellipsis", 
          }}
        >
          {displayText}
        </span>
      )}
      {children}
    </components.ValueContainer>
  );
};





const makeSelectStyles = ($light) => ({
  control: (base) => ({
    ...base,
    backgroundColor: $light ? "#FFFFFF" : "#191B27",
    borderColor: $light ? "#2D354A" : "#191B27",
    color: $light ? "#191B27" : "#DCE4F2",
    fontFamily: "Montserrat, sans-serif",
    fontSize: "14px",
    minHeight: 42,
  }),
  input: (base) => ({
    ...base,
    color: $light ? "#191B27" : "#DCE4F2",  // ✅ typed text visible
  }),
  placeholder: (base) => ({
    ...base,
    color: $light ? "#191B27" : "#DCE4F2",  // ✅ one clean placeholder
    fontStyle: "italic",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    backgroundColor: $light ? "#FFFFFF" : "#2D354A",
    zIndex: 9999,
  }),
  option: (base, { isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? "#0093FF22" : "transparent",
    color: $light ? "#191B27" : "#DCE4F2",
    cursor: "pointer",
  }),
  multiValue: () => ({ display: "none" }), // hide chips
});


// ---------------- Category Mapping ----------------
const CATEGORY_MAP = {
  "ALUM-PR": "Aluminum Wire (Price Inferred) Imports",
  "ALBW": "Aluminum Building Wire Imports",
  "PV": "Photovoltaic Wire Imports",
  "OHT": "Overhead Transmission Wire Imports",
  "COPP-PR": "Copper Wire (Price Inferred) Imports",
  "COPP": "Copper Wire Imports",
  "CORD": "Cord Imports",
  "RESI": "Residential Imports",
  "MV": "Medium Voltage Imports",
  "TRAY": "Tray Cable Imports",
  "SPEC": "Specialty Item Imports",
  "COMMS": "Communications Wire Imports",
  "TOOL": "Tool Imports",
  "RM": "Raw Material Imports",
  "UNMP": "Unmapped Imports",
  "CATH": "Copper Cathode",
  "PM": "Plastic-Aluminum-PR",
  "ASK": "Genergy Cables",
};

// ---------------- Component ----------------
function FilterBar({
  filters,
  setFilters,
  filteredData = [],
  processedData = [],
  isLightMode,
}) {
  // ✅ Build dynamic cascading options
  const dynamicOptions = useMemo(() => {
    const { category, buyer, seller, country } = filters;
    const uniq = (arr) => [...new Set(arr.filter((x) => x && x !== "Unknown"))];
    const alpha = (arr) => uniq(arr).sort((a, b) => a.localeCompare(b));

    const rowsForCategory = processedData.filter(
      (r) =>
        (!buyer.length || buyer.includes(r.buyer)) &&
        (!seller.length || seller.includes(r.seller)) &&
        (!country.length || country.includes(r.country))
    );

    const rowsForBuyer = processedData.filter(
      (r) =>
        (!category.length || category.includes(r.Category)) &&
        (!seller.length || seller.includes(r.seller)) &&
        (!country.length || country.includes(r.country))
    );

    const rowsForSeller = processedData.filter(
      (r) =>
        (!category.length || category.includes(r.Category)) &&
        (!buyer.length || buyer.includes(r.buyer)) &&
        (!country.length || country.includes(r.country))
    );

    const rowsForCountry = processedData.filter(
      (r) =>
        (!category.length || category.includes(r.Category)) &&
        (!buyer.length || buyer.includes(r.buyer)) &&
        (!seller.length || seller.includes(r.seller))
    );

    return {
      categories: alpha(rowsForCategory.map((r) => r.Category)),
      buyers: alpha(rowsForBuyer.map((r) => r.buyer)),
      sellers: alpha(rowsForSeller.map((r) => r.seller)),
      countries: alpha(rowsForCountry.map((r) => r.country)),
    };
  }, [filters, processedData]);

  const opts = useMemo(() => {
    const categoryOpts = (dynamicOptions.categories || []).map((abbr) => ({
      value: abbr,
      label: CATEGORY_MAP[abbr] || abbr,
    }));

    return {
      category: categoryOpts,
      buyer: (dynamicOptions.buyers || []).map((o) => ({ value: o, label: o })),
      seller: (dynamicOptions.sellers || []).map((o) => ({ value: o, label: o })),
      country: (dynamicOptions.countries || []).map((o) => ({ value: o, label: o })),
      charts: [
  { value: "buyers", label: "Top Domestic Importers" },
  { value: "categories", label: "Top Product Categories" },
  { value: "countries", label: "Country Distribution" },
  { value: "metricTonsOverTime", label: "Metric Tons Over Time" },
  { value: "ports", label: "Ports of Arrival" },
  { value: "sellers", label: "Top International Suppliers" },
  { value: "timeSeries", label: "Imports Over Time" },
].sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [dynamicOptions]);

  const [open, setOpen] = useState({
    category: false,
    buyer: false,
    seller: false,
    country: false,
    charts: false,
  });

  const refs = {
    category: useRef(null),
    buyer: useRef(null),
    seller: useRef(null),
    country: useRef(null),
    charts: useRef(null),
  };

  useEffect(() => {
    const handler = (e) => {
      if (!Object.values(open).some(Boolean)) return;
      const insideWrapper = Object.values(refs).some(
        (r) => r.current && r.current.contains(e.target)
      );
      if (insideWrapper) return;
      const insideMenu =
        e.target.closest(".sel-category__menu, .sel-category__menu-list") ||
        e.target.closest(".sel-buyer__menu, .sel-buyer__menu-list") ||
        e.target.closest(".sel-seller__menu, .sel-seller__menu-list") ||
        e.target.closest(".sel-country__menu, .sel-country__menu-list") ||
        e.target.closest(".sel-charts__menu, .sel-charts__menu-list");
      if (insideMenu) return;
      setOpen({
        category: false,
        buyer: false,
        seller: false,
        country: false,
        charts: false,
      });
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  const clearFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: [] }));
    setOpen((o) => ({ ...o, [key]: false }));
  };

  const styles = makeSelectStyles(isLightMode);

  const renderSelect = (name, label, classPrefix) => {
    const allOptions = opts[name] || [];
    const selectedValues = filters[name] || [];
    const selectedOptions = allOptions.filter((o) =>
      selectedValues.includes(o.value)
    );

    const isAllSelected =
      selectedOptions.length === allOptions.length && allOptions.length > 0;

    const handleChange = (selected) => {
      if (selected && selected.some((s) => s.value === "__all__")) {
        if (isAllSelected) {
          setFilters((prev) => ({ ...prev, [name]: [] }));
        } else {
          setFilters((prev) => ({
            ...prev,
            [name]: allOptions.map((o) => o.value),
          }));
        }
      } else {
        setFilters((prev) => ({
          ...prev,
          [name]: (selected || []).map((s) => s.value),
        }));
      }
    };

    const optionsWithSelectAll = [
      { value: "__all__", label: isAllSelected ? "Deselect All" : "Select All" },
      ...allOptions,
    ];

    return (
      <FilterGroup ref={refs[name]}>
        <FilterLabel $light={isLightMode}>{label}</FilterLabel>
        <Select
          classNamePrefix={classPrefix}
          isMulti
          isSearchable
          placeholder=""
          options={optionsWithSelectAll}
          value={selectedOptions}
          onChange={handleChange}
          styles={styles}
          components={{
            Option: CheckboxOption,
            ValueContainer: ValueContainerSummary,
          }}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          blurInputOnSelect={false}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          menuIsOpen={open[name]}
          onMenuOpen={() => setOpen((o) => ({ ...o, [name]: true }))}
          onMenuClose={() => setOpen((o) => ({ ...o, [name]: false }))}
          onFocus={() => setOpen((o) => ({ ...o, [name]: true }))}
        />
        <ClearButton $light={isLightMode} onClick={() => clearFilter(name)}>
          Clear Selections
        </ClearButton>
      </FilterGroup>
    );
  };

  // ✅ Final return
return (
  <FilterContainer className="filter-bar" $light={isLightMode}>
    <FilterGrid>
      {/* Dates */}
      {[ 
        { name: "startDate", label: "Start Date", type: "date", value: filters.startDate },
        { name: "endDate",   label: "End Date",   type: "date", value: filters.endDate },
      ].map((f) => (
        <FilterGroup key={f.name}>
          <FilterLabel $light={isLightMode}>{f.label}</FilterLabel>
          <FilterInput
            type={f.type}
            value={f.value}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, [f.name]: e.target.value }))
            }
            placeholder="MM/DD/YYYY"
            $light={isLightMode}
          />
        </FilterGroup>
      ))}

      {/* Dropdowns */}
      {renderSelect("category", "Category", "sel-category")}
      {renderSelect("buyer", "Importer", "sel-buyer")}
      {renderSelect("seller", "Exporter", "sel-seller")}
      {renderSelect("country", "Country of Origin", "sel-country")}
      {renderSelect("charts", "Charts", "sel-charts")}
    </FilterGrid>
  </FilterContainer>
);
}

export default FilterBar;

